import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { resetPassword } from "../../services/Authentication";
import FormComponent from "../forms";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";
import queriLogo from "../../assets/logo.png";

const ResetPasswordPage = () => {
  const { t } = useI18n();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (formData: Record<string, any>) => {
    if (formData.password !== formData.confirmPassword) {
      return {
        success: false,
        errors: [{ title: t.auth.errors.passwordsDoNotMatch }],
      };
    }

    if (formData.password.length < 8) {
      return {
        success: false,
        errors: [{ title: t.auth.errors.passwordTooShort }],
      };
    }

    if (!token) {
      return {
        success: false,
        errors: [{ title: t.auth.errors.invalidResetLink }],
      };
    }

    const response = await resetPassword(token, formData.password);

    if (!response.success) {
      return { success: false, errors: response.errors };
    }

    setSuccess(true);
    return { success: true };
  };

  if (success) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="w-full max-w-md space-y-6">
          <div className="flex flex-col items-center gap-1">
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold">{t.auth.resetPassword.success.title}</h1>
            <p className="text-sm text-muted-foreground text-center">
              {t.auth.resetPassword.success.subtitle}
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate("/")}>
            {t.auth.resetPassword.success.goToLogin}
          </Button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="w-full max-w-md space-y-6">
          <div className="flex flex-col items-center gap-1">
            <img
              src={queriLogo}
              alt="Queri"
              className="h-10 max-w-[240px] object-contain mb-3"
            />
            <h1 className="text-2xl font-bold">{t.auth.resetPassword.invalidToken.title}</h1>
            <p className="text-sm text-muted-foreground text-center">
              {t.auth.resetPassword.invalidToken.subtitle}
            </p>
          </div>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              {t.auth.resetPassword.invalidToken.requestNewLink}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-full">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-1">
          <img
            src={queriLogo}
            alt="Queri"
            className="h-10 max-w-[240px] object-contain mb-3"
          />
          <h1 className="text-2xl font-bold">{t.auth.resetPassword.title}</h1>
          <p className="text-sm text-muted-foreground">
            {t.auth.resetPassword.subtitle}
          </p>
        </div>
        <FormComponent onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">{t.auth.resetPassword.newPasswordLabel}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder={t.auth.resetPassword.newPasswordPlaceholder}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t.auth.resetPassword.confirmPasswordLabel}</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder={t.auth.resetPassword.confirmPasswordPlaceholder}
            />
          </div>
          <Alert>
            <AlertDescription>
              {t.auth.resetPassword.passwordRequirement}
            </AlertDescription>
          </Alert>
          <Button type="submit" className="w-full mt-2">
            {t.auth.resetPassword.submit}
          </Button>
        </FormComponent>
        <div className="text-center">
          <Link to="/" className="text-sm text-primary hover:underline">
            {t.auth.resetPassword.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
